import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const TM = () => <sup className="text-[0.6em] font-normal align-super">™</sup>;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

function CTAButton({ className = '' }: { className?: string }) {
  return (
    <Link
      to="/register"
      className={`inline-block bg-sage-600 hover:bg-sage-700 text-white font-semibold px-8 py-3.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-base tracking-wide ${className}`}
    >
      Start My Free Profile
    </Link>
  );
}

const layers = [
  {
    num: '1',
    title: <>Your Visual Profile</>,
    desc: 'A clear snapshot of your traits, emotional patterns, and growth areas — the starting point.',
    color: 'bg-sage-100 text-sage-700',
  },
  {
    num: '2',
    title: <>AI-Powered Insight</>,
    desc: 'Instant written interpretation designed to help you move from "I see my scores" to "I understand what they mean."',
    color: 'bg-teal-100 text-teal-700',
  },
  {
    num: '3',
    title: <>Talk With the Human Potential OS<TM /> AI Coach</>,
    desc: 'An AI-powered coaching companion you can ask questions, reflect with, and explore practical next steps — in your own words.',
    color: 'bg-amber-100 text-amber-700',
  },
  {
    num: '4',
    title: <>Review Your Results With a Trained Human Potential OS<TM /> Coach</>,
    desc: 'Where insight turns into direction. Your coach helps you make sense of it all and choose the next move that fits your life.',
    color: 'bg-chocolate-100 text-chocolate-700',
  },
];

export function DiscoverPage() {
  return (
    <div className="min-h-screen bg-[#faf8f4] font-urbanist text-chocolate-800">

      {/* ── Minimal header ── */}
      <header className="sticky top-0 z-30 bg-[#faf8f4]/90 backdrop-blur border-b border-chocolate-200/30">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center">
          <Link to="/">
            <img src="/HPOS_Logo_Whiter_Cream_V1.png" alt="Human Potential OS" className="h-8 w-auto" />
          </Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-5xl mx-auto px-5 pt-16 pb-12 lg:pt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            className="space-y-6"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <p className="text-sage-600 font-semibold text-sm tracking-widest uppercase">
              Free Human Potential Profile
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-chocolate-900 leading-[1.15]">
              Discover What's Really Driving Your Potential
            </h1>
            <div className="space-y-3 text-chocolate-600 text-lg leading-relaxed">
              <p>You've probably taken a personality test before.</p>
              <p>
                It told you what you already knew — gave you four letters, a colour, or a graph — and
                then it sat in a drawer.
              </p>
              <p className="font-semibold text-chocolate-800">This one is different.</p>
              <p>
                It doesn't just label you. It starts a conversation about who you are, how you operate,
                and what's quietly shaping your results.
              </p>
            </div>
            <CTAButton />
          </motion.div>

          <motion.div
            className="rounded-2xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut', delay: 0.15 }}
          >
            <img
              src="/images/lead-magnet/HPOS_Leadmag_Hero_Image.png"
              alt="Human Potential Profile dashboard"
              className="w-full h-auto object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* ── "This one is different" bridge ── */}
      <section className="bg-white/70 py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg order-2 lg:order-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/HPOS_Leadmag_Section_Image.png"
                alt="Labels versus a deeper map of human potential"
                className="w-full h-auto object-cover"
              />
            </motion.div>

            <motion.div
              className="space-y-5 order-1 lg:order-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <h2 className="text-3xl font-bold text-chocolate-900 leading-snug">
                You are not just a goal.<br />You are not just a problem.
              </h2>
              <p className="text-chocolate-600 text-lg leading-relaxed">
                You are a unique combination of strengths, patterns, emotions, habits, decisions,
                behaviours, and possibilities.
              </p>
              <p className="text-chocolate-600 text-lg leading-relaxed">
                The Human Potential Profile<TM /> is designed to help you see yourself with more clarity —
                what may be helping you move forward, what may be holding you back, and where your
                next breakthrough could begin.
              </p>
              <p className="font-semibold text-chocolate-800 text-lg">
                Most profiles give you a result. This one starts a conversation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Four Layers ── */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 space-y-10">
          <motion.div
            className="text-center space-y-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-sage-600 font-semibold text-sm tracking-widest uppercase">
              Most assessments hand you a graph and leave you to figure out what it means.
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-chocolate-900">
              The Human Potential Profile<TM /> gives you four layers of insight, each one building on the last.
            </h2>
          </motion.div>

          <motion.div
            className="rounded-2xl overflow-hidden shadow-xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
          >
            <img
              src="/images/lead-magnet/HPOS_Leadmag_Four_Layers_Image.png"
              alt="Four Layers of Insight infographic"
              className="w-full h-auto object-cover"
            />
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {layers.map((layer, i) => (
              <motion.div
                key={layer.num}
                className="bg-white rounded-xl border border-chocolate-100 p-6 shadow-sm space-y-3"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.5}
                variants={fadeUp}
              >
                <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${layer.color}`}>
                  {layer.num}
                </div>
                <h3 className="font-bold text-chocolate-900 text-lg leading-snug">{layer.title}</h3>
                <p className="text-chocolate-600 text-sm leading-relaxed">{layer.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            className="text-center text-chocolate-700 font-semibold text-lg"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            AI-powered insight. Human-led understanding. Personalised next steps.<br />
            <span className="text-sage-600">That's what makes this different.</span>
          </motion.p>
        </div>
      </section>

      {/* ── Sample Profile Preview ── */}
      <section className="bg-white/70 py-16">
        <div className="max-w-5xl mx-auto px-5 space-y-8">
          <motion.div
            className="text-center space-y-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-2xl lg:text-3xl font-bold text-chocolate-900">
              Here's what your profile looks like
            </h2>
            <p className="text-chocolate-500 text-base">
              Example only — your results will be personal to you.
            </p>
          </motion.div>
          <motion.div
            className="rounded-2xl overflow-hidden shadow-xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
          >
            <img
              src="/images/lead-magnet/ChatGPT_Image_Jun_17,_2026,_11_20_15_AM.png"
              alt="Sample Human Potential Profile preview showing characteristic scores, harmonic scale, AI insight summary, and AI coach chat"
              className="w-full h-auto object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* ── Why This Matters ── */}
      <section className="bg-white/70 py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <h2 className="text-3xl font-bold text-chocolate-900">Why This Matters</h2>
              <p className="text-chocolate-600 text-lg leading-relaxed">
                Most people don't stay stuck because they lack information.
              </p>
              <p className="text-chocolate-600 text-lg leading-relaxed">
                They stay stuck because a deeper pattern is quietly operating beneath the surface.
              </p>
              <p className="font-semibold text-chocolate-800 text-lg">
                The Human Potential Profile<TM /> helps you see it — so you can finally do something about it.
              </p>
            </motion.div>

            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/HPOS_Leadmag_Beneath_Surface_Patterns.png"
                alt="Deeper patterns beneath the surface"
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 space-y-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <h2 className="text-3xl font-bold text-chocolate-900">
                Begin Your Free Human Potential Profile<TM />
              </h2>
              <p className="text-chocolate-600 text-lg leading-relaxed">
                Your profile is free to complete. You'll receive a visual snapshot of your results
                and, depending on your coach's setup, the opportunity to explore them with
                AI-powered insights, talk with the Human Potential OS<TM /> AI Coach, and review
                everything with a trained Human Potential OS<TM /> Coach.
              </p>
              <CTAButton />
            </motion.div>

            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
            >
              <img
                src="/images/lead-magnet/HPOS_Leadmag_CTA_Image.png"
                alt="Your next step forward"
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Disclaimer ── */}
      <section className="border-t border-chocolate-200/40 bg-[#f5f2ec] py-10">
        <div className="max-w-3xl mx-auto px-5 space-y-3 text-xs text-chocolate-500 leading-relaxed">
          <p className="font-semibold text-chocolate-600 text-sm">Important Note</p>
          <p>
            The Human Potential Profile<TM /> is a coaching and personal development tool designed for
            reflection, insight, education, and growth.
          </p>
          <p>
            It is not a medical, psychological, psychiatric, diagnostic, legal, financial, or
            therapeutic assessment. Your results should not be used to diagnose, treat, or label any
            condition. They are intended as a starting point for coaching conversation, self-awareness,
            and personal development.
          </p>
          <p>
            AI-generated insights and AI Coach responses are for reflection and coaching support only.
            They should not be treated as professional medical, psychological, legal, financial, or
            therapeutic advice. A trained Human Potential OS<TM /> Coach can help you understand your
            results in context.
          </p>
          <p className="pt-2 text-chocolate-500">
            Prefer to talk it through first?{' '}
            <Link to="/coach-conversation" className="text-sage-600 hover:text-sage-700 underline font-medium">
              Start a free AI coaching conversation →
            </Link>
          </p>
        </div>
      </section>

    </div>
  );
}
